import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Building2, Receipt, Globe, Scale, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function Settings() {
  const [scaleConfig, setScaleConfig] = useState({
    enabled: false,
    barcode_prefix: '20',
    plu_digits: 5,
    price_digits: 5,
    price_includes_decimals: true,
    use_weight: false
  });
  const [saving, setSaving] = useState(false);
  const [savingSii, setSavingSii] = useState(false);
  const [savingFolios, setSavingFolios] = useState(false);
  const [testBarcode, setTestBarcode] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [siiConfig, setSiiConfig] = useState({
    rut: '',
    business_name: '',
    trade_name: '',
    address: '',
    commune: '',
    city: '',
    activity_code: '',
    resolution_number: '',
    resolution_date: '',
    environment: 'certificacion'
  });
  const [folios, setFolios] = useState({
    boleta: { next_folio: 1, is_active: false },
    factura: { next_folio: 1, is_active: false }
  });

  useEffect(() => {
    fetchInitialConfig();
  }, []);

  const fetchInitialConfig = async () => {
    await Promise.all([fetchScaleConfig(), fetchSiiConfig(), fetchFoliosConfig()]);
  };

  const fetchScaleConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/scale`, { withCredentials: true });
      setScaleConfig(data);
    } catch (error) {
      console.error('Error fetching scale config:', error);
    }
  };

  const saveScaleConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/scale`, scaleConfig, { withCredentials: true });
      toast.success('Configuración de balanza guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const fetchSiiConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/sii`, { withCredentials: true });
      setSiiConfig(data);
    } catch (error) {
      console.error('Error fetching SII config:', error);
    }
  };

  const fetchFoliosConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/folios`, { withCredentials: true });
      setFolios(data);
    } catch (error) {
      console.error('Error fetching folios config:', error);
    }
  };

  const saveSiiConfig = async () => {
    setSavingSii(true);
    try {
      await axios.put(`${API_URL}/api/settings/sii`, siiConfig, { withCredentials: true });
      toast.success('Configuración tributaria guardada');
      fetchSiiConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración tributaria');
    } finally {
      setSavingSii(false);
    }
  };

  const saveFolios = async () => {
    setSavingFolios(true);
    try {
      await Promise.all([
        axios.put(`${API_URL}/api/settings/folios/boleta`, folios.boleta, { withCredentials: true }),
        axios.put(`${API_URL}/api/settings/folios/factura`, folios.factura, { withCredentials: true })
      ]);
      toast.success('Folios internos actualizados');
      fetchFoliosConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar folios');
    } finally {
      setSavingFolios(false);
    }
  };

  const testScaleBarcode = async () => {
    if (!testBarcode) {
      toast.error('Ingresa un código de barras para probar');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/products/decode-scale-barcode?barcode=${testBarcode}`,
        {},
        { withCredentials: true }
      );
      setTestResult(data);
    } catch (error) {
      setTestResult({ success: false, error: 'Error al decodificar' });
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-stone-900 tracking-tight font-heading">Configuración</h1>
        <p className="text-stone-500 mt-1">Ajustes del sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Info */}
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-amber-600" />
              Datos del Negocio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-stone-500">Nombre</p>
              <p className="font-medium text-stone-900">Sweet Sales - Insumos de Pastelería</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">RUT</p>
              <p className="font-medium text-stone-900">76.XXX.XXX-X</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Dirección</p>
              <p className="font-medium text-stone-900">Av. Principal 123, Santiago</p>
            </div>
          </CardContent>
        </Card>

        {/* SII Configuration */}
        <Card className="bg-white border-stone-200 rounded-2xl lg:col-span-2" data-testid="sii-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5 text-blue-600" />
              Configuración Tributaria y Folios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-medium text-blue-800">Módulo pre-SII habilitado</p>
              <p className="text-sm text-blue-700 mt-1">
                Esta configuración deja listo el emisor, folios internos y estados tributarios para la futura conexión real con SII.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>RUT Emisor</Label>
                <Input
                  value={siiConfig.rut}
                  onChange={(e) => setSiiConfig({ ...siiConfig, rut: e.target.value })}
                  placeholder="76XXXXXXXX"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-rut-input"
                />
              </div>
              <div>
                <Label>Razón Social</Label>
                <Input
                  value={siiConfig.business_name}
                  onChange={(e) => setSiiConfig({ ...siiConfig, business_name: e.target.value })}
                  placeholder="Sweet Sales SpA"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-business-name-input"
                />
              </div>
              <div>
                <Label>Nombre de Fantasía</Label>
                <Input
                  value={siiConfig.trade_name}
                  onChange={(e) => setSiiConfig({ ...siiConfig, trade_name: e.target.value })}
                  placeholder="Sweet Sales"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-trade-name-input"
                />
              </div>
              <div>
                <Label>Código de Actividad</Label>
                <Input
                  value={siiConfig.activity_code}
                  onChange={(e) => setSiiConfig({ ...siiConfig, activity_code: e.target.value })}
                  placeholder="472100"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-activity-code-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Dirección</Label>
                <Input
                  value={siiConfig.address}
                  onChange={(e) => setSiiConfig({ ...siiConfig, address: e.target.value })}
                  placeholder="Av. Principal 123"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-address-input"
                />
              </div>
              <div>
                <Label>Comuna</Label>
                <Input
                  value={siiConfig.commune}
                  onChange={(e) => setSiiConfig({ ...siiConfig, commune: e.target.value })}
                  placeholder="Santiago"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-commune-input"
                />
              </div>
              <div>
                <Label>Ciudad</Label>
                <Input
                  value={siiConfig.city}
                  onChange={(e) => setSiiConfig({ ...siiConfig, city: e.target.value })}
                  placeholder="Santiago"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-city-input"
                />
              </div>
              <div>
                <Label>Número Resolución</Label>
                <Input
                  value={siiConfig.resolution_number}
                  onChange={(e) => setSiiConfig({ ...siiConfig, resolution_number: e.target.value })}
                  placeholder="80"
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-resolution-number-input"
                />
              </div>
              <div>
                <Label>Fecha Resolución</Label>
                <Input
                  type="date"
                  value={siiConfig.resolution_date}
                  onChange={(e) => setSiiConfig({ ...siiConfig, resolution_date: e.target.value })}
                  className="h-12 rounded-xl mt-1"
                  data-testid="sii-resolution-date-input"
                />
              </div>
              <div>
                <Label>Ambiente</Label>
                <Select
                  value={siiConfig.environment}
                  onValueChange={(value) => setSiiConfig({ ...siiConfig, environment: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="sii-environment-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="certificacion">Certificación</SelectItem>
                    <SelectItem value="produccion">Producción</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={saveSiiConfig}
              disabled={savingSii}
              className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              data-testid="save-sii-config-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              {savingSii ? 'Guardando...' : 'Guardar Configuración Tributaria'}
            </Button>

            <div className="border border-stone-200 rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-stone-900">Folios Internos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-xl space-y-3">
                  <p className="font-medium text-stone-900">Boleta</p>
                  <div>
                    <Label>Siguiente Folio</Label>
                    <Input
                      type="number"
                      min="1"
                      value={folios.boleta.next_folio}
                      onChange={(e) => setFolios({
                        ...folios,
                        boleta: { ...folios.boleta, next_folio: parseInt(e.target.value || '1', 10) }
                      })}
                      className="h-12 rounded-xl mt-1"
                      data-testid="boleta-next-folio-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Activo</Label>
                    <Switch
                      checked={folios.boleta.is_active}
                      onCheckedChange={(checked) => setFolios({
                        ...folios,
                        boleta: { ...folios.boleta, is_active: checked }
                      })}
                      data-testid="boleta-active-switch"
                    />
                  </div>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl space-y-3">
                  <p className="font-medium text-stone-900">Factura</p>
                  <div>
                    <Label>Siguiente Folio</Label>
                    <Input
                      type="number"
                      min="1"
                      value={folios.factura.next_folio}
                      onChange={(e) => setFolios({
                        ...folios,
                        factura: { ...folios.factura, next_folio: parseInt(e.target.value || '1', 10) }
                      })}
                      className="h-12 rounded-xl mt-1"
                      data-testid="factura-next-folio-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Activo</Label>
                    <Switch
                      checked={folios.factura.is_active}
                      onCheckedChange={(checked) => setFolios({
                        ...folios,
                        factura: { ...folios.factura, is_active: checked }
                      })}
                      data-testid="factura-active-switch"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={saveFolios}
                disabled={savingFolios}
                className="h-12 px-6 rounded-xl bg-stone-700 hover:bg-stone-800 text-white font-semibold"
                data-testid="save-folios-btn"
              >
                <Save className="w-5 h-5 mr-2" />
                {savingFolios ? 'Guardando...' : 'Guardar Folios'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Scale Configuration */}
        <Card className="bg-white border-stone-200 rounded-2xl lg:col-span-2" data-testid="scale-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Scale className="w-5 h-5 text-purple-600" />
              Configuración de Balanza Digital
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div>
                <p className="font-medium text-stone-900">Habilitar módulo de balanza</p>
                <p className="text-sm text-stone-500">Permite leer códigos de barras generados por balanzas digitales</p>
              </div>
              <Switch
                checked={scaleConfig.enabled}
                onCheckedChange={(checked) => setScaleConfig({ ...scaleConfig, enabled: checked })}
                data-testid="scale-enabled-switch"
              />
            </div>

            {scaleConfig.enabled && (
              <>
                {/* Configuration Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Prefijo del código</Label>
                    <Select
                      value={scaleConfig.barcode_prefix}
                      onValueChange={(value) => setScaleConfig({ ...scaleConfig, barcode_prefix: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="scale-prefix-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20 - Precio</SelectItem>
                        <SelectItem value="21">21 - Precio</SelectItem>
                        <SelectItem value="22">22 - Precio</SelectItem>
                        <SelectItem value="23">23 - Peso</SelectItem>
                        <SelectItem value="24">24 - Peso</SelectItem>
                        <SelectItem value="25">25 - Peso</SelectItem>
                        <SelectItem value="26">26 - Cantidad</SelectItem>
                        <SelectItem value="27">27 - Cantidad</SelectItem>
                        <SelectItem value="28">28 - Precio</SelectItem>
                        <SelectItem value="29">29 - Precio</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-stone-500 mt-1">Primeros 2 dígitos del código</p>
                  </div>

                  <div>
                    <Label>Dígitos del PLU</Label>
                    <Select
                      value={scaleConfig.plu_digits.toString()}
                      onValueChange={(value) => setScaleConfig({ ...scaleConfig, plu_digits: parseInt(value) })}
                    >
                      <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="scale-plu-digits">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 dígitos</SelectItem>
                        <SelectItem value="5">5 dígitos</SelectItem>
                        <SelectItem value="6">6 dígitos</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-stone-500 mt-1">Código del producto</p>
                  </div>

                  <div>
                    <Label>Dígitos del valor</Label>
                    <Select
                      value={scaleConfig.price_digits.toString()}
                      onValueChange={(value) => setScaleConfig({ ...scaleConfig, price_digits: parseInt(value) })}
                    >
                      <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="scale-price-digits">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 dígitos</SelectItem>
                        <SelectItem value="5">5 dígitos</SelectItem>
                        <SelectItem value="6">6 dígitos</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-stone-500 mt-1">Precio o peso</p>
                  </div>
                </div>

                {/* Additional options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                    <div>
                      <p className="font-medium text-stone-900">Valor incluye decimales</p>
                      <p className="text-sm text-stone-500">Ej: 01250 = $12.50</p>
                    </div>
                    <Switch
                      checked={scaleConfig.price_includes_decimals}
                      onCheckedChange={(checked) => setScaleConfig({ ...scaleConfig, price_includes_decimals: checked })}
                      data-testid="scale-decimals-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                    <div>
                      <p className="font-medium text-stone-900">El valor es peso (no precio)</p>
                      <p className="text-sm text-stone-500">Usar para venta por kg</p>
                    </div>
                    <Switch
                      checked={scaleConfig.use_weight}
                      onCheckedChange={(checked) => setScaleConfig({ ...scaleConfig, use_weight: checked })}
                      data-testid="scale-weight-switch"
                    />
                  </div>
                </div>

                {/* Format explanation */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-purple-800 mb-2">Formato esperado del código:</p>
                  <code className="text-sm bg-purple-100 px-2 py-1 rounded text-purple-900">
                    {scaleConfig.barcode_prefix}
                    {'X'.repeat(scaleConfig.plu_digits)}
                    {'V'.repeat(scaleConfig.price_digits)}
                    C
                  </code>
                  <p className="text-xs text-purple-700 mt-2">
                    Donde X = PLU del producto, V = {scaleConfig.use_weight ? 'Peso' : 'Precio'}, C = Dígito verificador
                  </p>
                </div>

                {/* Test barcode */}
                <div className="border border-stone-200 rounded-xl p-4">
                  <p className="font-medium text-stone-900 mb-3">Probar código de barras</p>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      value={testBarcode}
                      onChange={(e) => setTestBarcode(e.target.value)}
                      placeholder="Ej: 2000001012505"
                      data-testid="test-barcode-input"
                      className="h-12 rounded-xl flex-1"
                    />
                    <Button
                      onClick={testScaleBarcode}
                      variant="outline"
                      className="h-12 px-6 rounded-xl"
                      data-testid="test-barcode-btn"
                    >
                      Probar
                    </Button>
                  </div>
                  
                  {testResult && (
                    <div className={`mt-3 p-3 rounded-xl ${testResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                      {testResult.success ? (
                        <div className="text-sm">
                          <p className="font-medium text-emerald-800">Código válido</p>
                          <p className="text-emerald-700">PLU: {testResult.plu}</p>
                          <p className="text-emerald-700">
                            {testResult.value_type === 'weight' ? 'Peso' : 'Precio'}: {testResult.value}
                          </p>
                          <p className="text-emerald-700">Producto: {testResult.product?.name}</p>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <p className="font-medium text-red-800">Error</p>
                          <p className="text-red-700">{testResult.error}</p>
                          {testResult.plu && <p className="text-red-700">PLU detectado: {testResult.plu}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Save button */}
                <Button
                  onClick={saveScaleConfig}
                  disabled={saving}
                  data-testid="save-scale-config-btn"
                  className="h-12 px-6 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </>
            )}

            {!scaleConfig.enabled && (
              <Button
                onClick={saveScaleConfig}
                disabled={saving}
                data-testid="save-scale-config-btn"
                className="h-12 px-6 rounded-xl bg-stone-600 hover:bg-stone-700 text-white font-semibold"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* System Info */}
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <SettingsIcon className="w-5 h-5 text-stone-600" />
              Sistema
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-stone-500">Versión</p>
              <p className="font-medium text-stone-900">1.1.0</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Zona Horaria</p>
              <p className="font-medium text-stone-900">America/Santiago (UTC-3)</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Moneda</p>
              <p className="font-medium text-stone-900">CLP (Peso Chileno)</p>
            </div>
          </CardContent>
        </Card>

        {/* App Info */}
        <Card className="bg-white border-stone-200 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5 text-emerald-600" />
              Aplicación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-stone-500">Stack</p>
              <p className="font-medium text-stone-900">FastAPI + React + MongoDB</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Ambiente</p>
              <p className="font-medium text-stone-900">Producción</p>
            </div>
            <div>
              <p className="text-sm text-stone-500">Módulos</p>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">POS</span>
                <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">Inventario</span>
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">Caja</span>
                <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium">Balanza</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
